import { ANALYTICS_CONFIG, getDeviceType, getReferrerCategory } from './config';
import { STEP_IDS, STEP_LABELS, getErrorInfo } from './constants';
import SessionManager from './SessionManager';
import EventQueue, { upsertSession, updateSession } from './EventQueue';
import FieldTracker from './FieldTracker';

class AnalyticsService {
  constructor() {
    this.enabled = ANALYTICS_CONFIG.enabled;
    this.sessionManager = new SessionManager();
    this.eventQueue = new EventQueue();
    this.fieldTracker = new FieldTracker(this);
    this.sessionStarted = false;

    // Écouter la fermeture de page pour tracker l'abandon
    window.addEventListener('beforeunload', () => this._handleUnload());
    window.addEventListener('pagehide', () => this._handleUnload());
  }

  // ===================
  // API Publique
  // ===================

  // Appelé au démarrage de l'application
  async startSession() {
    if (!this.enabled || this.sessionStarted) return;

    this.sessionStarted = true;
    const sessionId = this.sessionManager.getSessionId();
    const context = this.sessionManager.getSessionContext();

    // Configurer l'EventQueue avec l'ID de session
    this.eventQueue.setSessionId(sessionId);

    // Créer la session dans Supabase (inclut les UTM pour faciliter les requêtes)
    await upsertSession({
      session_id: sessionId,
      device_type: getDeviceType(),
      referrer_category: getReferrerCategory(),
      utm_source: context.utm_source,
      utm_medium: context.utm_medium,
      utm_campaign: context.utm_campaign,
      utm_content: context.utm_content,
      user_profile: {},
      steps_visited: [],
      total_errors: 0
    });

    this._trackEvent('session_start', {
      ...context
    });
  }

  // Tracking des étapes
  trackStepStart(stepNumber) {
    if (!this.enabled) return;

    // Démarrer la session si pas encore fait
    if (!this.sessionStarted) {
      this.startSession();
    }

    const previousStep = this.sessionManager.getCurrentStep();
    this.sessionManager.startStep(stepNumber);
    this.fieldTracker.reset();

    this._trackEvent('step_start', {
      step_number: stepNumber,
      step_id: STEP_IDS[stepNumber],
      step_label: STEP_LABELS[stepNumber],
      is_first_visit: !this.sessionManager.hasVisitedStep(stepNumber),
      previous_step_id: previousStep ? STEP_IDS[previousStep] : null
    });
  }

  trackStepComplete(stepNumber) {
    if (!this.enabled) return;

    const timeOnStep = this.sessionManager.endStep(stepNumber);
    const errorCount = this.sessionManager.getErrorCount(stepNumber);
    const validationAttempts = this.sessionManager.getValidationAttempts(stepNumber);
    const fieldsModified = this.sessionManager.getFieldsModifiedCount();

    this._trackEvent('step_complete', {
      step_number: stepNumber,
      step_id: STEP_IDS[stepNumber],
      step_label: STEP_LABELS[stepNumber],
      time_on_step_ms: timeOnStep,
      validation_attempts: validationAttempts,
      error_count: errorCount,
      fields_modified_count: fieldsModified
    });
  }

  trackValidationAttempt(stepNumber) {
    if (!this.enabled) return;
    this.sessionManager.recordValidationAttempt(stepNumber);
  }

  // Tracking des erreurs
  trackValidationError(stepNumber, errorCode, fieldId = null) {
    if (!this.enabled) return;

    this.sessionManager.recordError(stepNumber, errorCode);
    const errorInfo = getErrorInfo(errorCode);
    const attemptNumber = this.sessionManager.getValidationAttempts(stepNumber);

    this._trackEvent('validation_error', {
      step_number: stepNumber,
      step_id: STEP_IDS[stepNumber],
      error_code: errorCode,
      error_type: errorInfo.type,
      field_id: fieldId || errorInfo.field,
      attempt_number: attemptNumber
    });
  }

  // Tracking des champs
  trackFieldFocus(stepNumber, fieldId) {
    if (!this.enabled) return;

    this.sessionManager.recordFieldFocus(fieldId);
    const timeSinceStepStart = this.sessionManager.getTimeOnCurrentStep();

    this._trackEvent('field_focus', {
      step_number: stepNumber,
      step_id: STEP_IDS[stepNumber],
      field_id: fieldId,
      time_since_step_start_ms: timeSinceStepStart
    });
  }

  trackFieldBlurEmpty(stepNumber, fieldId, focusDuration) {
    if (!this.enabled) return;

    this._trackEvent('field_blur_empty', {
      step_number: stepNumber,
      step_id: STEP_IDS[stepNumber],
      field_id: fieldId,
      focus_duration_ms: focusDuration
    });
  }

  trackFieldCompleted(stepNumber, fieldId, inputDuration) {
    if (!this.enabled) return;

    this._trackEvent('field_completed', {
      step_number: stepNumber,
      step_id: STEP_IDS[stepNumber],
      field_id: fieldId,
      input_duration_ms: inputDuration
    });
  }

  trackFieldCorrected(stepNumber, fieldId, timeToCorrect, previousErrorCode) {
    if (!this.enabled) return;

    this._trackEvent('field_corrected', {
      step_number: stepNumber,
      step_id: STEP_IDS[stepNumber],
      field_id: fieldId,
      time_to_correct_ms: timeToCorrect,
      previous_error_code: previousErrorCode
    });
  }

  // Tracking des sections conditionnelles
  trackConditionalShown(stepNumber, conditionalId, triggerField) {
    if (!this.enabled) return;

    this.sessionManager.recordConditionalShown(conditionalId, triggerField);
    const timeSinceStepStart = this.sessionManager.getTimeOnCurrentStep();

    this._trackEvent('conditional_shown', {
      step_number: stepNumber,
      step_id: STEP_IDS[stepNumber],
      condition_id: conditionalId,
      trigger_field: triggerField,
      time_since_step_start_ms: timeSinceStepStart
    });
  }

  // Tracking de la navigation
  trackStepBack(fromStep, toStep) {
    if (!this.enabled) return;

    const timeOnCurrentStep = this.sessionManager.getTimeOnCurrentStep();

    this._trackEvent('step_back', {
      from_step_number: fromStep,
      from_step_id: STEP_IDS[fromStep],
      to_step_number: toStep,
      to_step_id: STEP_IDS[toStep],
      time_on_current_step_ms: timeOnCurrentStep
    });
  }

  trackStepSkip(fromStep, toStep) {
    if (!this.enabled) return;

    const skippedSteps = [];
    const start = Math.min(fromStep, toStep);
    const end = Math.max(fromStep, toStep);
    for (let i = start + 1; i < end; i++) {
      skippedSteps.push(i);
    }

    this._trackEvent('step_skip', {
      from_step_number: fromStep,
      from_step_id: STEP_IDS[fromStep],
      to_step_number: toStep,
      to_step_id: STEP_IDS[toStep],
      skipped_steps: skippedSteps,
      reason: 'dashboard_click'
    });
  }

  trackFormRestart(abandonedAtStep) {
    if (!this.enabled) return;

    const completedSteps = this.sessionManager.getCompletedSteps();
    const totalTime = this.sessionManager.getSessionDuration();

    this._trackEvent('form_restart', {
      abandoned_at_step: abandonedAtStep,
      abandoned_at_step_id: STEP_IDS[abandonedAtStep],
      completed_steps: completedSteps,
      total_time_ms: totalTime
    });

    // Reset la session
    this.sessionManager.reset();
    this.sessionStarted = false;
  }

  // Tracking des bounces (utilisateurs qui quittent sans remplir)
  trackBounce() {
    if (!this.enabled) return;

    // Démarrer la session si pas encore fait (pour avoir les données de contexte)
    if (!this.sessionStarted) {
      this.startSession();
    }

    const context = this.sessionManager.getSessionContext();

    this._trackEvent('session_bounce', {
      total_duration_ms: this.sessionManager.getSessionDuration(),
      device_type: context.device_type,
      referrer_category: context.referrer_category,
      viewport_width: context.viewport_width,
      viewport_height: context.viewport_height
    });

    // Flush immédiat
    this.eventQueue.flush(true);
  }

  // Tracking d'abandon de session (utilisateurs qui ont commencé mais pas terminé)
  trackSessionAbandon(currentStep, demandeur = {}, famille = {}, menage = {}) {
    if (!this.enabled) return;

    const userProfile = this.sessionManager.getUserProfile();
    const completedSteps = this.sessionManager.getCompletedSteps();
    const fieldState = this.fieldTracker.getCurrentState();

    // Construire le profil utilisateur partiel à partir des données disponibles
    const partialProfile = {
      ...userProfile,
      has_age: !isNaN(demandeur.age),
      has_partner: famille.en_couple,
      housing_type: menage.statut_occupation_logement,
      department: menage.code_postal ? menage.code_postal.substring(0, 2) : null
    };

    this._trackEvent('session_abandon', {
      total_duration_ms: this.sessionManager.getSessionDuration(),
      abandoned_at_step: currentStep,
      abandoned_at_step_id: STEP_IDS[currentStep],
      last_field_focused: fieldState.focusedField,
      completed_steps: completedSteps,
      total_errors: this.sessionManager.getTotalErrors(),
      user_profile: partialProfile
    });

    // Flush immédiat
    this.eventQueue.flush(true);
  }

  // Tracking de fin de session
  async trackSessionComplete(benefitsCount = 0) {
    if (!this.enabled) return;

    const sessionId = this.sessionManager.getSessionId();
    const userProfile = this.sessionManager.getUserProfile();
    const completedSteps = this.sessionManager.getCompletedSteps();
    const totalDuration = this.sessionManager.getSessionDuration();
    const totalErrors = this.sessionManager.getTotalErrors();
    const totalAttempts = this.sessionManager.getTotalValidationAttempts();

    // IMPORTANT: Flush d'abord tous les événements en attente (step_complete, etc.)
    // AVANT de marquer la session comme complète
    await this.eventQueue.flush(true);

    this._trackEvent('session_complete', {
      total_duration_ms: totalDuration,
      steps_completed: completedSteps,
      total_errors: totalErrors,
      total_validation_attempts: totalAttempts,
      benefits_count: benefitsCount,
      user_profile: userProfile
    });

    // Flush l'événement session_complete
    await this.eventQueue.flush(true);

    // Mettre à jour la session comme complète dans Supabase APRÈS tous les événements
    await updateSession(sessionId, {
      completed: true,
      ended_at: new Date().toISOString(),
      benefits_count: benefitsCount,
      user_profile: userProfile,
      steps_visited: completedSteps.map(s => STEP_IDS[s]),
      total_errors: totalErrors
    });
  }

  // Mise à jour du profil utilisateur (appelé par les formulaires)
  // Envoie immédiatement à Supabase pour avoir les données même en cas d'abandon
  async updateUserProfile(data) {
    if (!this.enabled) return;
    this.sessionManager.updateUserProfile(data);

    // Synchroniser avec Supabase pour avoir les données en cas d'abandon
    const sessionId = this.sessionManager.getSessionId();
    const userProfile = this.sessionManager.getUserProfile();
    await updateSession(sessionId, { user_profile: userProfile });
  }

  setUserAge(age) {
    if (!this.enabled) return;
    this.sessionManager.setAge(age);
  }

  setUserHasChildren(hasChildren, childrenCount = 0) {
    if (!this.enabled) return;
    this.sessionManager.setHasChildren(hasChildren, childrenCount);
  }

  setUserHasPartner(hasPartner) {
    if (!this.enabled) return;
    this.sessionManager.setHasPartner(hasPartner);
  }

  setUserHousingType(housingType) {
    if (!this.enabled) return;
    this.sessionManager.setHousingType(housingType);
  }

  setUserDepartment(postalCode) {
    if (!this.enabled) return;
    this.sessionManager.setDepartment(postalCode);
  }

  setUserSituation(situations) {
    if (!this.enabled) return;
    this.sessionManager.setSituation(situations);
  }

  setUserIsEmployed(isEmployed) {
    if (!this.enabled) return;
    this.sessionManager.setIsEmployed(isEmployed);
  }

  // Getter pour le field tracker (utilisé par les composants)
  getFieldTracker() {
    return this.fieldTracker;
  }

  // ===================
  // Méthodes internes
  // ===================

  _trackEvent(eventName, data) {
    const event = {
      event_id: this._generateUUID(),
      session_id: this.sessionManager.getSessionId(),
      timestamp: new Date().toISOString(),
      event: eventName,
      ...data
    };

    this.eventQueue.add(event);
  }

  _handleUnload() {
    if (!this.enabled || !this.sessionStarted) return;

    const currentStep = this.sessionManager.getCurrentStep();

    // Ne pas tracker l'abandon si on est aux résultats (step 7)
    if (currentStep && currentStep < 7) {
      const fieldState = this.fieldTracker.getCurrentState();
      const userProfile = this.sessionManager.getUserProfile();
      const completedSteps = this.sessionManager.getCompletedSteps();

      // Abandon d'étape
      this._trackEvent('step_abandon', {
        step_number: currentStep,
        step_id: STEP_IDS[currentStep],
        step_label: STEP_LABELS[currentStep],
        time_on_step_ms: this.sessionManager.getTimeOnCurrentStep(),
        last_field_focused: fieldState.focusedField,
        last_field_value_entered: fieldState.hasValue,
        validation_attempts: this.sessionManager.getValidationAttempts(currentStep),
        completed_steps: completedSteps
      });

      // Abandon de session
      this._trackEvent('session_abandon', {
        total_duration_ms: this.sessionManager.getSessionDuration(),
        abandoned_at_step: currentStep,
        abandoned_at_step_id: STEP_IDS[currentStep],
        last_field_focused: fieldState.focusedField,
        completed_steps: completedSteps,
        total_errors: this.sessionManager.getTotalErrors(),
        user_profile: userProfile
      });
    }

    // Flush synchrone
    this.eventQueue.flush(true);
  }

  _generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }

  // Désactiver le tracking
  disable() {
    this.enabled = false;
    this.eventQueue.clear();
  }

  // Activer le tracking
  enable() {
    this.enabled = true;
  }

  isEnabled() {
    return this.enabled;
  }
}

// Singleton
const analyticsService = new AnalyticsService();
export default analyticsService;
